import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, WrenchIcon } from 'lucide-react';
import { RxMark } from './PrescriptionBadge';
import { CtaButton } from './CtaButton';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO, rxInsertImages } from '../data/rxInsert';
import { lenses } from '../data/product';
import { trackCustom } from '../utils/tracking';

/**
 * Prescription ready is a buying reason today. The RX Insert is shown as a
 * separate, future accessory so nobody thinks they should wait for it.
 */
export function PrescriptionSection() {
  const updatesHref = RX_INSERT_UPDATES_URL.trim() || RX_INSERT_FALLBACK_MAILTO;
  const isExternal = /^https?:\/\//.test(updatesHref);
  const photos = rxInsertImages.filter(image => image.src.trim());
  return <section id="prescription" aria-labelledby="prescription-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" data-id="element-709" data-mp="as77xsdbb0ew">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-710" data-mp="fsmo0rozam0k">
        <div className="flex items-center gap-3" data-id="element-711" data-mp="2f8u8u091gv6q">
          <RxMark className="h-10 w-10 shrink-0" data-id="element-712" data-mp="2f9m419yq4qwh" />
          <p className="font-display text-xl font-extrabold uppercase tracking-wide text-[#7fe3ec]" data-id="element-713" data-mp="2b78zupzml11t">Prescription ready</p>
        </div>
        <h2 id="prescription-heading" className="grit-headline mt-4 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-714" data-mp="2p3vw0qjr7n6o">
          Your vision. Your prescription.
          <br data-id="element-715" data-mp="2y4ank7nb9yim" />
          <span className="text-grit-500" data-id="element-716" data-mp="2u5bh2ilxltp2">Your X1.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-steel-300" data-id="element-717" data-mp="2u2z2mffdu1gr">
          Built with prescription users in mind. Prescription users can buy the X1 today.
        </p>

        <p className="mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-steel-400" data-id="element-718" data-mp="32yojqc9ss9oc">Two ways to go prescription</p>
        <div className="mt-4 grid gap-px bg-white/10 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-719" data-mp="3huw2d85k531i">
          {/* Primary: available now */}
          <div className="flex flex-col bg-[#07242a] p-6 sm:p-8" data-id="element-720" data-mp="3jyrfbk1sw07l">
            <p className="inline-flex self-start bg-[#7fe3ec] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-ink-950" data-id="element-721" data-mp="3k0kmd0gbsmg4">
              Available now
            </p>
            <h3 className="mt-4 font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-722" data-mp="3tw5kuom6r4l4">
              Prescription ready
            </h3>
            <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-steel-200" data-id="element-723" data-mp="7i49c7edp1vj">
              Wear prescription lenses? Take your X1 to your trusted optical professional and ask about a custom
              prescription setup.
            </p>
            <ul className="mt-5 grid gap-2" data-id="element-724" data-mp="giu4r9zq8q8q">
              {['Buy your X1 today', 'Your optical professional handles the prescription', 'Clear, Amber and Smoke lenses stay in the box'].map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-725" data-mp="2n8h1q0s4him6">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#7fe3ec]" aria-hidden="true" data-id="element-726" data-mp="2fopnwnmsvze4" />
                    {line}
                  </li>)}
            </ul>
            <div className="mt-auto pt-7" data-id="element-727" data-mp="25ywmgfgdh0nl">
              <CtaButton className="w-full sm:w-auto" data-id="element-728" data-mp="25zo7wxg99i05">Choose your X1 kit</CtaButton>
              <Link to="/legal/terms" className="mt-3 block text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-729" data-mp="2114j8paa1s43">
                Terms apply.
              </Link>
            </div>
          </div>

          {/* Secondary: in development */}
          <div className="flex flex-col bg-ink-900 p-6 sm:p-8" data-id="element-730" data-mp="2ope1jh8a7xqh">
            <p className="inline-flex items-center gap-1.5 self-start border border-grit-500/60 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-grit-500" data-id="element-731" data-mp="2ont8xx5j2ov9">
              <WrenchIcon className="h-3 w-3" aria-hidden="true" data-id="element-732" data-mp="1dba8cd9iqdmh" />
              In development
            </p>
            <h3 className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-733" data-mp="189uqeu2n0n8b">
              GritVue X1 RX Insert™
            </h3>
            <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-734" data-mp="31xd7wkdwn5dt">
              Prototype in development · The next step in prescription integration
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-735" data-mp="2s7jz1r55d4d9">
              A removable prescription platform designed specifically for the X1. It is intended to sit behind the
              main interchangeable lens, so your prescription can stay in place while you switch outer lenses.
            </p>
            <p className="mt-4 flex flex-wrap items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-736" data-mp="35xdn478tvklx">
              {lenses.map((lens, i) => <React.Fragment key={lens.name} data-id="element-737" data-mp="1uiguyohckfac" data-mpi={i}>
                  {i > 0 && <span className="text-steel-500" data-id="element-738" data-mp="1ul31cksi4oqz">·</span>}
                  <span className="flex items-center gap-1.5" data-id="element-739" data-mp="1zimetihdkxhv">
                    <span className="h-3 w-3 rounded-full border border-white/25" style={{
                  backgroundColor: lens.swatch
                }} aria-hidden="true" data-id="element-740" data-mp="hjosi7v18k65" />
                    {lens.name}
                  </span>
                </React.Fragment>)}
            </p>

            <div className="mt-6 border-t border-white/10 pt-5" data-id="element-741" data-mp="mk2pmuv3i9pe">
              <p className="font-display text-lg font-bold uppercase text-grit-500" data-id="element-742" data-mp="3uyb8vcbq9o4w">Why are we building it?</p>
              <p className="mt-2 text-[14px] leading-relaxed text-steel-300" data-id="element-743" data-mp="3q1b5j969izhh">
                Because prescription users should be able to keep their corrective solution in place while changing
                the outer X1 lens for the job. The goal: cleaner, simpler, removable and more integrated with the X1
                platform.
              </p>
              <p className="mt-3 text-[13px] text-steel-400" data-id="element-744" data-mp="379ibdbfgj886">
                Not required to use the X1. Not included, and not shipping yet.
              </p>
            </div>

            <a href={updatesHref} {...isExternal ? {
            target: '_blank',
            rel: 'noopener noreferrer'
          } : {}} onClick={() => trackCustom('rx_insert_updates_clicked')} className="mt-6 inline-flex items-center justify-center gap-2 self-start border-2 border-grit-500 px-5 py-3 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-745" data-mp="1a6tb439n2j8b">
              Get RX Insert updates
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-746" data-mp="11aamh38fxhhj" />
            </a>
          </div>
        </div>

        {photos.length > 0 && <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-747" data-mp="2co5yw47ync52">
            {photos.map(photo => <li key={photo.caption} data-id="element-748" data-mp="2hkwdso8141ow">
                <figure className="border border-white/10 bg-ink-900" data-id="element-749" data-mp="5z4am9idk341">
                  <img src={photo.src} alt={photo.alt} className="aspect-square w-full object-contain" loading="lazy" data-id="element-750" data-mp="5yazkwcdurg3" />
                  <figcaption className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-751" data-mp="12dnx3c2e99n">
                    {photo.caption} · Prototype
                  </figcaption>
                </figure>
              </li>)}
          </ul>}
      </div>
    </section>;
}